import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useMemo, useState } from "react";
import { ArrowDown, ArrowUp, Eye, Pencil, ShieldCheck, Trash2, UserRound } from "lucide-react";
import { toast } from "sonner";
import {
  createUser,
  listUsers,
  updateUserProfile,
  deleteUser,
  type ManagedUser,
} from "@/lib/users.functions";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { getMe } from "@/lib/core.functions";
import { EmptyState, PageHeader } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const Route = createFileRoute("/_authenticated/users")({
  head: () => ({
    meta: [
      { title: "User management | Leadership Edge Scheduling" },
      {
        name: "description",
        content: "Admin view of every account that has signed in, with role and access controls.",
      },
      { property: "og:title", content: "User management | Leadership Edge Scheduling" },
      { property: "og:description", content: "Manage roles and account access for the scheduling console." },
    ],
  }),
  component: UsersPage,
});

type SortKey = "email" | "fullName" | "phone" | "role" | "active" | "lastSignInAt" | "createdAt";
const PAGE_SIZE = 20;

interface EditForm {
  fullName: string;
  phone: string;
  notes: string;
  role: "admin" | "trainer";
  isTrainer: boolean;
  active: boolean;
}

const EMPTY_FORM: EditForm = {
  fullName: "",
  phone: "",
  notes: "",
  role: "trainer",
  isTrainer: false,
  active: true,
};

const ROLE_LABEL: Record<"admin" | "trainer", string> = { admin: "Admin", trainer: "Trainer" };

function formatDate(value: string | null) {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "—"
    : date.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

function UsersPage() {
  const queryClient = useQueryClient();
  const fetchMe = useServerFn(getMe);
  const fetchUsers = useServerFn(listUsers);
  const saveProfile = useServerFn(updateUserProfile);
  const addUser = useServerFn(createUser);

  const navigate = useNavigate();
  const { data: me } = useQuery({ queryKey: ["me"], queryFn: () => fetchMe() });

  useEffect(() => {
    if (me && !me.isAdmin) navigate({ to: "/dashboard", replace: true });
  }, [me, navigate]);
  const { data, isPending, error } = useQuery({ queryKey: ["users"], queryFn: () => fetchUsers() });

  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("email");
  const [sortAsc, setSortAsc] = useState(true);
  const [page, setPage] = useState(0);
  const [detail, setDetail] = useState<ManagedUser | null>(null);
  const [editTarget, setEditTarget] = useState<ManagedUser | null>(null);
  const [form, setForm] = useState<EditForm>(EMPTY_FORM);
  const [addOpen, setAddOpen] = useState(false);
  const [newUser, setNewUser] = useState({ email: "", fullName: "", phone: "" });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["users"] });
    queryClient.invalidateQueries({ queryKey: ["trainers"] });
  };

  const addMutation = useMutation({
    mutationFn: () => addUser({ data: newUser }),
    onSuccess: () => {
      toast.success("User added. They become active once they sign in with Google.");
      setAddOpen(false);
      setNewUser({ email: "", fullName: "", phone: "" });
      invalidate();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const removeUser = useServerFn(deleteUser);
  const deleteMutation = useMutation({
    mutationFn: (userId: string) => removeUser({ data: { userId } }),
    onSuccess: () => {
      toast.success("User deleted.");
      invalidate();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const profileMutation = useMutation({
    mutationFn: (payload: EditForm & { userId: string }) => saveProfile({ data: payload }),
    onSuccess: () => {
      toast.success("Profile updated.");
      setEditTarget(null);
      invalidate();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  function openEdit(user: ManagedUser) {
    setEditTarget(user);
    setForm({
      fullName: user.fullName ?? "",
      phone: user.phone ?? "",
      notes: user.notes ?? "",
      role: user.role === "admin" ? "admin" : "trainer",
      isTrainer: user.isTrainer,
      active: user.active,
    });
  }



  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    const list = (data?.users ?? []).filter((u) =>
      !term
        ? true
        : [u.email, u.fullName, u.phone, u.role, u.provider].some((v) =>
            (v ?? "").toLowerCase().includes(term),
          ),
    );
    const dir = sortAsc ? 1 : -1;
    return [...list].sort((a, b) => {
      if (sortKey === "active") return (Number(a.active) - Number(b.active)) * dir;
      const av = (a[sortKey] ?? "") as string;
      const bv = (b[sortKey] ?? "") as string;
      return av.localeCompare(bv) * dir;
    });
  }, [data, search, sortKey, sortAsc]);

  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const current = Math.min(page, pageCount - 1);
  const visible = rows.slice(current * PAGE_SIZE, current * PAGE_SIZE + PAGE_SIZE);

  function toggleSort(key: SortKey) {
    if (key === sortKey) setSortAsc((v) => !v);
    else {
      setSortKey(key);
      setSortAsc(true);
    }
    setPage(0);
  }

  function SortButton({ label, sortField }: { label: string; sortField: SortKey }) {
    const active = sortKey === sortField;
    return (
      <button
        type="button"
        onClick={() => toggleSort(sortField)}
        className="inline-flex items-center gap-1 text-left font-medium hover:text-foreground"
      >
        {label}
        {active ? (
          sortAsc ? (
            <ArrowUp className="h-3.5 w-3.5" aria-hidden="true" />
          ) : (
            <ArrowDown className="h-3.5 w-3.5" aria-hidden="true" />
          )
        ) : null}
      </button>
    );
  }

  return (
    <>
      <PageHeader
        eyebrow="Administration"
        title="User management"
        description="Everyone in the system, whether they have signed in with Google yet or not. Set someone as Admin or Trainer, mark an admin as a trainer too, and activate or deactivate access without touching scheduling data."
        actions={
          <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
            <Input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              placeholder="Search name, email, role"
              className="w-full sm:w-72"
              aria-label="Search users"
            />
            <Button onClick={() => setAddOpen(true)}>Add user</Button>
          </div>
        }
      />

      {error ? (
        <EmptyState title="Users could not be loaded" body={(error as Error).message} />
      ) : isPending ? (
        <EmptyState title="Loading users" body="Fetching accounts from the authentication service." />
      ) : rows.length === 0 ? (
        <EmptyState title="No users found" body="Accounts appear here after their first Google sign-in." />
      ) : (
        <div className="space-y-4">
          <div className="overflow-x-auto rounded-lg border border-border bg-surface/60">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead><SortButton label="User" sortField="fullName" /></TableHead>
                  <TableHead><SortButton label="Email" sortField="email" /></TableHead>
                  <TableHead><SortButton label="Role" sortField="role" /></TableHead>
                  <TableHead>Trainer</TableHead>
                  <TableHead><SortButton label="Status" sortField="active" /></TableHead>
                  <TableHead className="hidden md:table-cell">
                    <SortButton label="Last sign-in" sortField="lastSignInAt" />
                  </TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visible.map((u) => {
                  const isSelf = u.id === me?.userId;
                  return (
                    <TableRow key={u.id}>
                      <TableCell className="font-medium">
                        <span className="flex items-center gap-2">
                          {u.role === "admin" ? (
                            <ShieldCheck className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                          ) : (
                            <UserRound className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                          )}
                          {u.fullName ?? "—"}
                          {isSelf ? <Badge variant="secondary">You</Badge> : null}
                        </span>
                      </TableCell>
                      <TableCell className="text-muted-foreground">{u.email ?? "—"}</TableCell>
                      <TableCell>
                        <Badge variant={u.role === "admin" ? "default" : "secondary"}>
                          {u.role ? ROLE_LABEL[u.role] : "No role yet"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={u.isTrainer ? "default" : "secondary"}>
                          {u.isTrainer ? "On" : "Off"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap items-center gap-1">
                          <Badge variant={u.active ? "default" : "destructive"}>
                            {u.active ? "Active" : "Inactive"}
                          </Badge>
                          {u.hasLogin ? null : <Badge variant="secondary">Not signed in</Badge>}
                        </div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell text-muted-foreground">
                        {formatDate(u.lastSignInAt)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex flex-wrap justify-end gap-2">
                          <Button variant="soft" size="sm" onClick={() => setDetail(u)} title="View" aria-label="View">
                            <Eye className="h-4 w-4" />
                            View
                          </Button>
                          <Button variant="soft" size="sm" onClick={() => openEdit(u)} title="Edit" aria-label="Edit">
                            <Pencil className="h-4 w-4" />
                            Edit
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            title="Delete"
                            aria-label="Delete"
                            disabled={deleteMutation.isPending}
                            onClick={() => {
                              const name = u.fullName ?? u.email ?? "this user";
                              if (
                                window.confirm(
                                  `Delete ${name}? Their teaching blocks, course permissions and time off are removed, and their scheduled sessions become unassigned. This cannot be undone.`,
                                )
                              )
                                deleteMutation.mutate(u.id);
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                            Delete
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-muted-foreground">
              {rows.length} user{rows.length === 1 ? "" : "s"} · page {current + 1} of {pageCount}
            </p>
            <div className="flex gap-2">
              <Button variant="soft" size="sm" disabled={current === 0} onClick={() => setPage(current - 1)}>
                Previous
              </Button>
              <Button
                variant="soft"
                size="sm"
                disabled={current >= pageCount - 1}
                onClick={() => setPage(current + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        </div>
      )}

      <Dialog open={!!detail} onOpenChange={(open) => (open ? null : setDetail(null))}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{detail?.fullName ?? detail?.email ?? "User"}</DialogTitle>
            <DialogDescription>Account details from the authentication service.</DialogDescription>
          </DialogHeader>
          {detail ? (
            <dl className="grid gap-3 text-sm sm:grid-cols-2">
              {[
                ["Email", detail.email ?? "—"],
                ["Phone", detail.phone ?? "—"],
                ["Role", detail.role ? ROLE_LABEL[detail.role] : "No role yet"],
                ["Status", detail.active ? "Active" : "Inactive"],
                ["Signed in with Google", detail.hasLogin ? "Yes" : "Not yet"],
                ["Sign-in provider", detail.provider ?? "—"],
                ["Trainer", detail.isTrainer ? "On" : "Off"],
                ["Email confirmed", formatDate(detail.emailConfirmedAt)],
                ["Created", formatDate(detail.createdAt)],
                ["Last sign-in", formatDate(detail.lastSignInAt)],
                ["User ID", detail.id],
              ].map(([label, value]) => (
                <div key={label}>
                  <dt className="eyebrow">{label}</dt>
                  <dd className="mt-0.5 break-words text-foreground">{value}</dd>
                </div>
              ))}
            </dl>
          ) : null}
          <DialogFooter>
            <Button variant="soft" onClick={() => setDetail(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editTarget} onOpenChange={(open) => (open ? null : setEditTarget(null))}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit profile</DialogTitle>
            <DialogDescription>{editTarget?.email ?? editTarget?.fullName ?? "User"}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Name</Label>
              <Input
                id="edit-name"
                value={form.fullName}
                onChange={(e) => setForm({ ...form, fullName: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-phone">Phone number</Label>
              <Input
                id="edit-phone"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="(602) 555-0134"
                inputMode="tel"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-role">Role</Label>
              <Select
                value={form.role}
                onValueChange={(role) =>
                  setForm({
                    ...form,
                    role: role as "admin" | "trainer",
                    isTrainer: role === "trainer" ? true : form.isTrainer,
                  })
                }
                disabled={editTarget?.id === me?.userId}
              >
                <SelectTrigger id="edit-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin" disabled={!editTarget?.hasLogin}>
                    Admin
                  </SelectItem>
                  <SelectItem value="trainer">Trainer</SelectItem>
                </SelectContent>
              </Select>
              {editTarget?.hasLogin ? null : (
                <p className="text-xs text-muted-foreground">
                  Admin access is available once they have signed in with Google.
                </p>
              )}
            </div>
            {form.role === "admin" ? (
              <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
                <div>
                  <p className="text-sm font-medium">Also a Trainer</p>
                  <p className="text-xs text-muted-foreground">
                    Turn on to include this admin in scheduling.
                  </p>
                </div>
                <Switch
                  checked={form.isTrainer}
                  onCheckedChange={(checked) => setForm({ ...form, isTrainer: checked })}
                  aria-label="Also a Trainer"
                />
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">
                A Trainer is always included in scheduling.
              </p>
            )}
            <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
              <div>
                <p className="text-sm font-medium">Status</p>
                <p className="text-xs text-muted-foreground">
                  {form.active ? "Active" : "Inactive — they cannot sign in or be scheduled."}
                </p>
              </div>
              <Switch
                checked={form.active}
                disabled={editTarget?.id === me?.userId}
                onCheckedChange={(checked) => setForm({ ...form, active: checked })}
                aria-label="Active"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-notes">Notes</Label>
              <Textarea
                id="edit-notes"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Scheduling notes or anything worth remembering."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="soft" onClick={() => setEditTarget(null)}>
              Cancel
            </Button>
            <Button
              disabled={profileMutation.isPending || form.fullName.trim().length < 2}
              onClick={() =>
                editTarget &&
                profileMutation.mutate({
                  userId: editTarget.id,
                  ...form,
                  fullName: form.fullName.trim(),
                  phone: form.phone.trim(),
                  notes: form.notes.trim(),
                })
              }
            >
              {profileMutation.isPending ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add user</DialogTitle>
            <DialogDescription>
              They stay inactive until they sign in with Google using this email address, which links to
              this record instead of creating a second one.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="new-user-name">Full name</Label>
              <Input
                id="new-user-name"
                value={newUser.fullName}
                onChange={(e) => setNewUser({ ...newUser, fullName: e.target.value })}
                placeholder="Jordan Reed"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-user-email">Email</Label>
              <Input
                id="new-user-email"
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                placeholder="jordan@lookingforwardconsulting.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-user-phone">Phone number</Label>
              <Input
                id="new-user-phone"
                value={newUser.phone}
                onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })}
                placeholder="(602) 555-0134"
                inputMode="tel"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              New people are added as a Trainer. Admin access can only be granted after they have signed
              in with Google.
            </p>
          </div>
          <DialogFooter>
            <Button variant="soft" onClick={() => setAddOpen(false)}>
              Cancel
            </Button>
            <Button
              disabled={addMutation.isPending || !newUser.email.trim() || newUser.fullName.trim().length < 2}
              onClick={() => addMutation.mutate()}
            >
              Add user
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
